import { useEffect, useMemo, useState, useRef } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { signInEmailSchema, SignInEmailType } from '@/schemes/sign-in-email-schema';
import { AxiosError } from 'axios';
import { SignInEmailForm } from '../sign-in-email-form';
import { SignInOtpForm } from '../sign-in-otp-form';
import { authApi } from '@/api';
import { useToast } from '@/store/useToast';
import { ErrorNotificationIcon } from '@/assets/icons';
import { useTranslation } from 'react-i18next';
import { useSource } from '@/store/useSource';
import { RequestOtpPayload } from '@/api/auth';

interface Props {
    onLogin: () => void
}

export const SignInForm: React.FC<Props> = ({ onLogin }) => {
    const CODE_SEND_TIMEOUT_ERROR = 'code_send_timeout'
    const EMAIL_FIELD = 'email'
    const EMAIL_STEP = 1
    const OTP_STEP = 2
    const DEFAULT_RESEND_CODE_TIMEOUT = 60
    
    const [isLoading, setIsLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [step, setStep] = useState(EMAIL_STEP)
    const [resendCodeDelay, setResendCodeDelay] = useState(DEFAULT_RESEND_CODE_TIMEOUT)
    const [secondsBeforeOTPRequest, setSecondsBeforeOTPRequest] = useState<number | null>(null)
    const [otpInterval, setOtpInterval] = useState<NodeJS.Timeout | null>(null)

    const { source } = useSource()

    useEffect(() => {
        if (otpInterval) clearTimeout(otpInterval)
        setOtpInterval(null)
    }, [])

    const initialValues = useMemo(() => {
        return { [EMAIL_FIELD]: '' };
    }, []);

    const signInEmailMethods = useForm<SignInEmailType>({
        resolver: zodResolver(signInEmailSchema),
        defaultValues: initialValues,
    });

    const updateTimer = () => {
        const interval = setTimeout(
            () => {
                clearTimeout(interval)
                setSecondsBeforeOTPRequest(seconds => {
                    let val = resendCodeDelay
                    if (seconds) val = --seconds
                    if (val <= 0) {
                        if (interval) {
                            clearTimeout(interval)
                            setOtpInterval(null)
                        }
                        return null
                    }
                    setOtpInterval(updateTimer())
                    return val
                })
            },
            1000
        )
        return interval
    }
    
    const startOtpResendCodeTimer = () => {
        setOtpInterval(() => {
            setSecondsBeforeOTPRequest(resendCodeDelay)
            return updateTimer()
        })
    }

    const onReturnToEmailInput = () => {
        setError(null)
        setStep(EMAIL_STEP)
    }

    const showToast = useToast(state => state.show);

    const { t } = useTranslation()

    const showErrorToast = () => {
        const CONTENT_PADDING_BOTTOM = 48
        const BTN_HEIGHT = 64
        const GAP = 16
        showToast({
            icon: <ErrorNotificationIcon />,
            msg: t('error_try_again'), bottom: CONTENT_PADDING_BOTTOM + BTN_HEIGHT + GAP
        })
    }

    const prepareRequestOtpPayload = (): RequestOtpPayload => {
        const payload: RequestOtpPayload = {
            profile: {
                email: signInEmailMethods.getValues(EMAIL_FIELD)
            }
        }
        if (source) payload.profile.source = source
        return payload
    }

    const sendCode = async () => {
        setError(null)
        try {
            setIsLoading(true);
            const res =
                await authApi.requestOTP(
                    prepareRequestOtpPayload()
                );
            setResendCodeDelay(res.data.countdown)
            startOtpResendCodeTimer()
            setStep(OTP_STEP)
        } catch (error) {
            if (error instanceof AxiosError) {
                if (
                    error.response?.status === 422 &&
                    error.response?.data.errors
                ) {
                    // code already was requested to current email and otp timeout not expired
                    if (error.response?.data.errors.includes(CODE_SEND_TIMEOUT_ERROR)) {
                        if (otpInterval) {
                            clearTimeout(otpInterval)
                            setOtpInterval(null)
                        }
                        setResendCodeDelay(error.response?.data.countdown)
                        startOtpResendCodeTimer()
                        setStep(OTP_STEP)
                    }
                    setError(error.response?.data.message)
                } else showErrorToast()
            }
        } finally {
            setIsLoading(false);
        }
    }

    const resendCode = async () => {
        setError(null)
        try {
            setIsLoading(true);
            const res =
                await authApi.requestOTP(
                    prepareRequestOtpPayload()
                );
            setResendCodeDelay(res.data.countdown)
            startOtpResendCodeTimer()
        } catch (error) {
            if (error instanceof AxiosError) {
                if (
                    error.response?.status === 422 &&
                    error.response?.data.errors
                ) {
                    // force show common error for otp form (email or otp errors) without redirect to email form
                    setError(error.response?.data.message)
                } else showErrorToast()
            }
        } finally {
            setIsLoading(false);
        }
    }
    
    switch (step) {
        case EMAIL_STEP:
            return (
                <SignInEmailForm
                    signInEmailMethods={signInEmailMethods}
                    isLoading={isLoading}
                    error={error}
                    setError={setError}
                    sendCode={sendCode} />
            );
        case OTP_STEP:
            return (
                <SignInOtpForm
                    secondsBeforeOTPRequest={secondsBeforeOTPRequest}
                    email={signInEmailMethods.getValues(EMAIL_FIELD)}
                    error={error}
                    setError={setError}
                    returnToEmailInput={onReturnToEmailInput}
                    showErrorToast={showErrorToast}
                    resendCode={resendCode}
                    onLogin={onLogin} />
            );
        default:
            return <>Error 404</>;
    }
}
